/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import {
    Component,
    EventEmitter,
    forwardRef,
    Inject,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';
import { isUndefined } from 'underscore';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Upload } from 'ajs/modules/core/factories/upload';
import { NotificationService } from 'ng/modules/core/services/notification.service';
import { L10nService } from '@vmw/ngx-vip';
import { normalizeValue } from '../../utils';
import { LoadingState } from '../async-file-upload/async-file-upload.component';
import * as l10n from './async-file-upload-with-upload-service.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TUpload = typeof Upload;

/**
 * @description
 *     Component that allows the user to select and upload a file to the controller. The ngModel is
 *     populated with the filePath and the file name.
 * @author alextsg, Manideep Kotha
 */
@Component({
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AsyncFileUploadWithUploadServiceComponent),
        },
    ],
    selector: 'async-file-upload-with-upload-service',
    templateUrl: './async-file-upload-with-upload-service.component.html',
})
export class AsyncFileUploadWithUploadServiceComponent implements ControlValueAccessor, OnDestroy {
    /**
     * Max file size passed to the FileUploadComponent.
     */
    @Input()
    public maxFileSize = Infinity;

    /**
     * File path, used for uploading the file to the backend.
     */
    @Input()
    public filePath = '';

    /**
     * File API, used for uploading the file to the backend.
     */
    @Input()
    public fileApi = '';

    /**
     * Value for the accept HTML attribute. Used to limit the selectable files from the file
     * browser.
     */
    @Input()
    public accept: string;

    /**
     * If true, enables the Cancel button during file upload.
     */
    @Input()
    public enableCancel = false;

    /**
     * Emits event with error when new upload error comes from backend api.
     */
    @Output()
    public onError = new EventEmitter<string>();

    /**
     * File name passed to the file-upload component. Displayed to the user to show that a file has
     * been selected/uploaded.
     */
    public fileName: string;

    /**
     * Selected file, passed as the ngModel to the file-upload component.
     */
    public file: File;

    /**
     * Errors from uploading the file.
     */
    public errors: string;

    /**
     * If true, disables both the file select and upload buttons.
     */
    public disabled = false;

    /**
     * Loading state of the Upload button.
     */
    public loadingState = LoadingState.DEFAULT;

    /**
     * Get keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Value being get/set as the ngModel value. Set to the <file-path>/<file-name>.
     */
    private modelValue: string;

    /**
     * Injected upload service
     */
    private readonly upload: Upload;

    constructor(
        private readonly l10nService: L10nService,
        @Inject(Upload) UploadFactory: TUpload,
        private readonly notificationService: NotificationService,
    ) {
        this.upload = new UploadFactory();
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnDestroy(): void {
        if (this.upload.isInProgress()) {
            this.upload.cancelUpload();
        }
    }

    /**
     * Getter for the modelValue.
     */
    public get value(): string | undefined {
        return this.modelValue;
    }

    /**
     * Setter for the modelValue.
     */
    public set value(val: string) {
        if (this.modelValue !== val) {
            const normalizedValue = normalizeValue(val);

            this.modelValue = normalizedValue;
            this.onChange(normalizedValue);
            this.fileName = isUndefined(normalizedValue) ? normalizedValue : this.getFileName();
        }

        this.onTouched();
    }

    /**
     * Called after clicking the Upload button.
     */
    public async handleFileUpload(): Promise<void> {
        if (this.errors) {
            this.onError.emit('');
        }

        const {
            file,
            filePath,
            fileApi,
        } = this;
        const { name: fileName } = file;

        this.loadingState = LoadingState.LOADING;
        this.errors = '';

        try {
            await this.upload.send(file as File, fileName, fileApi, filePath);
            this.value = `${filePath}/${fileName}`;

            this.notificationService.addSimpleNotification(
                this.l10nService.getMessage(l10nKeys.fileUploadedSuccessfullyHeader),
                fileName,
            );
        } catch (errors) {
            this.errors = this.upload.error;
            this.onError.emit(this.errors);
        } finally {
            this.loadingState = LoadingState.DEFAULT;
        }
    }

    /**
     * Called after clicking the Cancel button while upload is in progress.
     */
    public handleFileUploadCancel(): void {
        this.upload.cancelUpload();
        this.loadingState = LoadingState.DEFAULT;
    }

    /**
     * Handler for file select change.
     */
    public handleFileSelectChange(file: File): void {
        this.value = undefined;

        if (file) {
            this.fileName = file.name;
        }
    }

    /**
     * ngModel value in the async-file-upload component is the selected file.
     * While editing we have just the file name, not the entire file
     * so when we remove the file while editing, just fileName gets changed and
     * ngModel value in the async-file-upload component doesn't change.
     * that's why ngModelChange is not getting called and
     * we are not using ngModelChange handler here.
     *
     * Method to reset model value in parent component if selected file is removed.
     */
    public handleFileNameChange(fileName: string): void {
        if (!fileName) {
            this.value = undefined;
        }
    }

    /***************************************************************************
     * IMPLEMENTING ControlValueAccessor INTERFACE
    */

    /**
     * Sets the onChange function.
     */
    public registerOnChange(fn: (value: string) => {}): void {
        this.onChange = fn;
    }

    /**
     * Writes the modelValue.
     */
    public writeValue(value: string): void {
        const normalizedValue = normalizeValue(value);

        this.modelValue = normalizedValue;
        this.fileName = isUndefined(normalizedValue) ? normalizedValue : this.getFileName();
    }

    /**
     * Sets the onTouched function.
     */
    public registerOnTouched(fn: () => {}): void {
        this.onTouched = fn;
    }

    /**
     * Function that is called by the forms API when the control status changes to or from
     * 'DISABLED'. Depending on the status, it enables or disables the appropriate DOM element.
     */
    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    /*************************************************************************/

    /**
     * Method to be overridden by the ControlValueAccessor interface.
     */
    private onChange = (value: string): void => {};

    /**
     * Method to be overridden by the ControlValueAccessor interface.
     */
    private onTouched = (): void => {};

    /**
     * Returns the file name, based on the ngModel value minus the given file path.
     */
    private getFileName(): string {
        return this.modelValue ? this.modelValue.replace(`${this.filePath}/`, '') : '';
    }
}
